<template>
  <div class="max-w-3xl mx-auto space-y-3 py-3">
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <div class="flex items-center justify-between mb-2">
        <div>
          <h2 class="text-base font-semibold text-gray-800">Auto-Generate Matriculation Numbers</h2>
          <p class="text-xs text-gray-500">Automatically assign matric/admission numbers when students are created, imported, or admitted.</p>
        </div>
        <ToggleSwitch v-model="config.enabled" />
      </div>

      <template v-if="config.enabled">
        <Divider />

        <div class="space-y-2.5">
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-0.5">Prefix</label>
            <InputText v-model="config.prefix" placeholder="e.g. SHP, UNI, ADM" class="w-full md:w-1/2" />
            <small class="text-gray-500 block mt-0.5">A short code identifying your school.</small>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-700 mb-0.5">Template Format</label>
            <InputText v-model="config.template" placeholder="{PREFIX}/{YY}{SEQ:6}" class="w-full md:w-1/2 font-mono" />
            <small class="text-gray-500 block mt-0.5">
              Available placeholders:
            </small>
            <div class="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
              <div v-for="ph in placeholders" :key="ph.token"
                class="flex items-start gap-1.5 bg-gray-50 rounded-lg px-2 py-1.5">
                <code class="text-primary font-mono font-semibold whitespace-nowrap">{{ ph.token }}</code>
                <span class="text-gray-600">{{ ph.description }}</span>
              </div>
            </div>
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-700 mb-0.5">Next Sequence Number</label>
            <InputNumber v-model="config.next_sequence" :min="1" class="w-full md:w-1/3" />
            <small class="text-gray-500 block mt-0.5">The sequence counter increments with each new student.</small>
          </div>

          <div class="bg-primary/10 border border-primary/20 rounded-lg p-2.5">
            <div class="flex items-center gap-1.5 mb-1">
              <i class="pi pi-eye text-primary text-sm"></i>
              <span class="text-xs font-semibold text-primary-700">Preview</span>
            </div>
            <p class="text-base font-mono text-primary-800">{{ livePreview }}</p>
            <small class="text-primary">This is what the next generated matric number will look like.</small>
          </div>
        </div>
      </template>
    </div>

    <div class="flex justify-end">
      <Button label="Save" icon="pi pi-check" size="small" @click="save" :loading="saving" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable';
import { gql } from '@apollo/client/core';
import { useToast } from 'primevue/usetoast';
import { UPDATE_SCHOOL_SETTINGS } from '@/graphql';
import { useMutation } from '@vue/apollo-composable';

const toast = useToast();

const MATRIC_NO_CONFIG_QUERY = gql`
  query MatricNoConfig {
    matricNoConfig {
      enabled
      template
      prefix
      next_sequence
      preview
    }
  }
`;

const config = ref({
  enabled: false,
  template: '{PREFIX}/{YY}{SEQ:6}',
  prefix: '',
  next_sequence: 1,
});

const { result, loading } = useQuery(MATRIC_NO_CONFIG_QUERY, null, { fetchPolicy: 'network-only' });
const { mutate: updateSettings, loading: saving } = useMutation(UPDATE_SCHOOL_SETTINGS);

onMounted(() => {
  // Watch for query result
});

watch(result, (val) => {
  if (val?.matricNoConfig) {
    const c = val.matricNoConfig;
    config.value = {
      enabled: c.enabled,
      template: c.template,
      prefix: c.prefix,
      next_sequence: c.next_sequence,
    };
  }
});

const placeholders = [
  { token: '{PREFIX}', description: 'Your school prefix' },
  { token: '{YY}', description: '2-digit year (e.g. 26)' },
  { token: '{YYYY}', description: '4-digit year (e.g. 2026)' },
  { token: '{SEQ:N}', description: 'Sequence padded to N digits' },
  { token: '{SEQ}', description: 'Sequence without padding' },
];

const livePreview = computed(() => {
  const { template, prefix, next_sequence } = config.value;
  const now = new Date();
  let result = template
    .replace('{PREFIX}', prefix)
    .replace('{YYYY}', now.getFullYear().toString())
    .replace('{YY}', now.getFullYear().toString().slice(-2));

  result = result.replace(/\{SEQ:(\d+)\}/g, (_: string, n: string) =>
    String(next_sequence).padStart(parseInt(n), '0'),
  );
  result = result.replace('{SEQ}', String(next_sequence));

  return result;
});

async function save() {
  try {
    await updateSettings({
      settings: {
        matric_no: {
          enabled: config.value.enabled,
          template: config.value.template,
          prefix: config.value.prefix,
          next_sequence: config.value.next_sequence,
        },
      },
    });
    toast.add({ severity: 'success', summary: 'Saved', detail: 'Matric number settings updated.', life: 3000 });
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e instanceof Error ? e.message : 'Failed to save settings.', life: 3000 });
  }
}
</script>
